import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import {
  ImageError,
  prepareSource,
  readCrop,
  type ImagePreset,
  type SourceARecadrer,
} from '../../utils/image';
import { t } from '../../i18n';

/**
 * Choisir ce que le rond montrera.
 *
 * Une photo de profil finit dans un disque, et jusqu'ici c'est le code
 * qui decidait lequel : `readImage` redimensionnait l'image entiere, le
 * cadre rond en prenait le milieu, et un visage pose dans un coin s'en
 * trouvait coupe sans que personne n'ait rien pu dire. On rend donc le
 * geste : la photo se deplace et s'agrandit sous le doigt, le disque ne
 * bouge pas, et « Choisir » entérine ce qu'on voit.
 *
 * Rien ne passe par React pendant le geste — meme parti que
 * `usePinchZoom` : une position d'etat, ce serait un rendu de l'arbre a
 * chaque image pour deplacer un seul noeud. L'echelle et le decalage
 * vivent dans des refs, et seul « Choisir » vient les lire.
 */

/** Au-dela, on ne cadre plus une photo, on cadre des pixels. */
const ZOOM_MAX = 4;

/** Part du cote le plus court occupee par le disque. */
const PART_DU_CADRE = 0.84;

/** Bornes du disque : lisible sur un petit ecran, sobre sur un grand. */
const DIAMETRE_MIN = 160;
const DIAMETRE_MAX = 340;

interface Point {
  x: number;
  y: number;
}

function borner(valeur: number, bas: number, haut: number): number {
  return Math.min(haut, Math.max(bas, valeur));
}

export function PhotoCropper({
  file,
  preset,
  onCancel,
  onChoose,
  onError,
}: {
  file: File;
  preset: ImagePreset;
  onCancel: () => void;
  /** Rend la photo cadree. Le recadrage reste ouvert tant qu'elle n'est pas partie. */
  onChoose: (dataUrl: string) => void | Promise<void>;
  onError?: (message: string) => void;
}) {
  const [source, setSource] = useState<SourceARecadrer | null>(null);
  const [diametre, setDiametre] = useState(0);
  const [busy, setBusy] = useState(false);
  /**
   * La photo est a l'ecran.
   *
   * `readCrop` dessine la balise `<img>` : tant qu'elle n'a pas fini de
   * charger, elle ne porte aucun pixel et le decoupage rendrait un carre
   * blanc. Le bouton attend donc de pouvoir tenir sa promesse.
   */
  const [affichee, setAffichee] = useState(false);

  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  /** L'echelle choisie par le doigt, a partir de celle qui couvre le disque. */
  const zoomRef = useRef(1);
  /** Le decalage du centre de la photo par rapport au centre du disque. */
  const decalageRef = useRef<Point>({ x: 0, y: 0 });

  /*
    Le recadrage se pose dans le cadre de l'application plutot qu'a
    l'endroit ou vit le declencheur : celui-ci est enfoui dans une zone
    defilante, et un voile `position: absolute` s'y accrocherait — c'est
    le meme piege que les feuilles d'actions, qui passent pour cela par la
    fente `overlay` des gabarits. Un portail evite d'avoir a percer cette
    fente jusqu'ici depuis les cinq ecrans qui changent une photo.
  */
  const [hote] = useState(
    () => document.querySelector<HTMLElement>('.app-frame') ?? document.body,
  );

  /* Le fichier decode une fois, redresse et borne. */
  useEffect(() => {
    let vivant = true;
    let prete: SourceARecadrer | null = null;

    prepareSource(file)
      .then((resultat) => {
        prete = resultat;
        if (vivant) setSource(resultat);
        else resultat.oublier();
      })
      .catch((cause) => {
        if (!vivant) return;
        onError?.(
          cause instanceof ImageError ? cause.message : t.composants.photo.pasOuverte,
        );
        onCancel();
      });

    return () => {
      vivant = false;
      prete?.oublier();
    };
    // Seul le fichier compte : les rappels changent d'identite a chaque
    // rendu du parent, et les suivre relancerait le decodage pour rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  /* Le disque se mesure sur la place reellement disponible. */
  useLayoutEffect(() => {
    const zone = stageRef.current;
    if (!zone) return;

    const mesurer = () => {
      const cote = Math.min(zone.clientWidth, zone.clientHeight) * PART_DU_CADRE;
      setDiametre(Math.round(borner(cote, DIAMETRE_MIN, DIAMETRE_MAX)));
    };

    mesurer();
    // Rotation du telephone, ou clavier qui se retire : le disque suit.
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, []);

  /** L'echelle a laquelle la photo couvre tout juste le disque. */
  const couverture = source && diametre ? Math.max(diametre / source.width, diametre / source.height) : 0;

  /* Le geste : un doigt deplace, deux doigts agrandissent. */
  useEffect(() => {
    const noeud = stageRef.current;
    if (!noeud || !couverture || !source) return;
    /*
      Deux constantes deja restreintes : les fonctions ci-dessous sont
      remontees en tete de bloc, et TypeScript leur rend alors le type
      declare — celui qui accepte encore `null`.
    */
    const zone = noeud;
    const photo = source;

    zoomRef.current = 1;
    decalageRef.current = { x: 0, y: 0 };

    /* L'etat au debut du geste : tout se calcule par rapport a lui. */
    let zoom0 = 1;
    let decalage0: Point = { x: 0, y: 0 };
    let ecart0 = 0;
    let depart: Point = { x: 0, y: 0 };
    let glisse = false;
    let image = 0;

    /** Un point de l'ecran, ramene au centre du cadre. */
    function relatif(x: number, y: number): Point {
      const cadre = zone.getBoundingClientRect();
      return { x: x - (cadre.left + cadre.width / 2), y: y - (cadre.top + cadre.height / 2) };
    }

    function poser() {
      if (image) return;
      image = requestAnimationFrame(() => {
        image = 0;
        const noeud = imgRef.current;
        if (!noeud) return;
        const { x, y } = decalageRef.current;
        noeud.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${zoomRef.current})`;
      });
    }

    /**
     * Le disque reste couvert.
     *
     * On retient la photo des que l'un de ses bords entre dans le cadre :
     * au-dela on decouperait du vide, et l'avatar sortirait avec un coin
     * blanc. C'est aussi ce qui rend le geste lisible — la photo bute,
     * elle ne s'echappe pas.
     */
    function retenir() {
      const echelle = couverture * zoomRef.current;
      const maxX = Math.max(0, (photo.width * echelle - diametre) / 2);
      const maxY = Math.max(0, (photo.height * echelle - diametre) / 2);
      decalageRef.current = {
        x: borner(decalageRef.current.x, -maxX, maxX),
        y: borner(decalageRef.current.y, -maxY, maxY),
      };
    }

    /** Change d'echelle en gardant fixe le point vise. */
    function versEchelle(cible: number, vise: Point) {
      const zoom = zoomRef.current;
      const ancre = {
        x: (vise.x - decalageRef.current.x) / zoom,
        y: (vise.y - decalageRef.current.y) / zoom,
      };
      zoomRef.current = cible;
      decalageRef.current = { x: vise.x - ancre.x * cible, y: vise.y - ancre.y * cible };
      retenir();
      poser();
    }

    function ecart(a: Touch, b: Touch): number {
      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    function onTouchStart(event: TouchEvent) {
      if (event.touches.length === 2) {
        const [a, b] = [event.touches[0], event.touches[1]];
        ecart0 = ecart(a, b);
        depart = relatif((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
        zoom0 = zoomRef.current;
        decalage0 = decalageRef.current;
        return;
      }
      if (event.touches.length !== 1) return;
      const doigt = event.touches[0];
      depart = relatif(doigt.clientX, doigt.clientY);
      decalage0 = decalageRef.current;
    }

    function onTouchMove(event: TouchEvent) {
      // Le geste est a nous : sans cela WebKit tenterait de faire defiler
      // l'ecran de dessous, et le doigt lacherait la photo en chemin.
      event.preventDefault();

      if (event.touches.length === 2) {
        const [a, b] = [event.touches[0], event.touches[1]];
        if (!ecart0) return;
        const milieu = relatif((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
        const zoom = borner((zoom0 * ecart(a, b)) / ecart0, 1, ZOOM_MAX);
        zoomRef.current = zoom;
        decalageRef.current = {
          x: milieu.x - ((depart.x - decalage0.x) * zoom) / zoom0,
          y: milieu.y - ((depart.y - decalage0.y) * zoom) / zoom0,
        };
        retenir();
        poser();
        return;
      }

      if (event.touches.length !== 1) return;
      const doigt = event.touches[0];
      const ici = relatif(doigt.clientX, doigt.clientY);
      decalageRef.current = {
        x: decalage0.x + (ici.x - depart.x),
        y: decalage0.y + (ici.y - depart.y),
      };
      retenir();
      poser();
    }

    function onTouchEnd(event: TouchEvent) {
      const doigt = event.touches[0];
      if (!doigt) return;
      /*
        Un doigt reste : le geste continue, mais son origine change. Sans
        ce rebasage, relacher un doigt d'un pincement ferait sauter la
        photo de tout le chemin deja parcouru.
      */
      ecart0 = 0;
      depart = relatif(doigt.clientX, doigt.clientY);
      decalage0 = decalageRef.current;
    }

    /* La souris, pour le banc d'essai : glisser deplace, la molette agrandit. */
    function onMouseDown(event: MouseEvent) {
      event.preventDefault();
      glisse = true;
      depart = relatif(event.clientX, event.clientY);
      decalage0 = decalageRef.current;
    }

    function onMouseMove(event: MouseEvent) {
      if (!glisse) return;
      const ici = relatif(event.clientX, event.clientY);
      decalageRef.current = {
        x: decalage0.x + (ici.x - depart.x),
        y: decalage0.y + (ici.y - depart.y),
      };
      retenir();
      poser();
    }

    function onMouseUp() {
      glisse = false;
    }

    function onWheel(event: WheelEvent) {
      event.preventDefault();
      const cible = borner(zoomRef.current * Math.exp(-event.deltaY / 240), 1, ZOOM_MAX);
      versEchelle(cible, relatif(event.clientX, event.clientY));
    }

    zone.addEventListener('touchstart', onTouchStart, { passive: true });
    zone.addEventListener('touchmove', onTouchMove, { passive: false });
    zone.addEventListener('touchend', onTouchEnd, { passive: true });
    zone.addEventListener('touchcancel', onTouchEnd, { passive: true });
    zone.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    zone.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      if (image) cancelAnimationFrame(image);
      zone.removeEventListener('touchstart', onTouchStart);
      zone.removeEventListener('touchmove', onTouchMove);
      zone.removeEventListener('touchend', onTouchEnd);
      zone.removeEventListener('touchcancel', onTouchEnd);
      zone.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      zone.removeEventListener('wheel', onWheel);
    };
  }, [source, diametre, couverture]);

  /* Echap referme, et rien ne defile derriere — comme une feuille. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    document.documentElement.classList.add('sheet-open');

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.documentElement.classList.remove('sheet-open');
    };
  }, [onCancel]);

  /** Ce que le disque montre, rendu dans les pixels de la source. */
  async function choisir() {
    const noeud = imgRef.current;
    if (!source || !noeud || !couverture) return;

    setBusy(true);
    try {
      const echelle = couverture * zoomRef.current;
      const cote = diametre / echelle;
      const zone = {
        x: borner(
          source.width / 2 - decalageRef.current.x / echelle - cote / 2,
          0,
          Math.max(0, source.width - cote),
        ),
        y: borner(
          source.height / 2 - decalageRef.current.y / echelle - cote / 2,
          0,
          Math.max(0, source.height - cote),
        ),
        cote,
      };
      await onChoose(readCrop(noeud, zone, preset));
    } catch (cause) {
      onError?.(
        cause instanceof Error ? cause.message : t.composants.photo.pasPreparee,
      );
    } finally {
      setBusy(false);
    }
  }

  const largeur = source ? Math.round(source.width * couverture) : 0;
  const hauteur = source ? Math.round(source.height * couverture) : 0;

  const vue = (
    <div
      className="crop"
      role="dialog"
      aria-modal="true"
      aria-label={t.composants.photo.recadrer}
      /*
        Le portail deplace le voile dans le DOM, mais pas dans l'arbre des
        composants : React fait toujours remonter ses evenements le long
        de celui-ci. Sans cette coupure, un deplacement parti du bord
        gauche atteindrait le `SubScreen` qui contient le declencheur, et
        cadrer sa photo ferait revenir en arriere.
      */
      onTouchStart={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
      onTouchEnd={(event) => event.stopPropagation()}
    >
      <div className="crop__stage" ref={stageRef}>
        {source && couverture > 0 && (
          <img
            className="crop__photo"
            ref={imgRef}
            src={source.url}
            alt=""
            draggable={false}
            onLoad={() => setAffichee(true)}
            style={{
              width: largeur,
              height: hauteur,
              marginLeft: -largeur / 2,
              marginTop: -hauteur / 2,
            }}
          />
        )}
        <span
          className="crop__ring"
          aria-hidden="true"
          style={{ width: diametre, height: diametre }}
        />
      </div>

      <div className="crop__actions">
        <button type="button" className="crop__cancel" onClick={onCancel} disabled={busy}>
          {t.composants.photo.annuler}
        </button>
        <Button
          variant="primary"
          size="md"
          loading={busy}
          disabled={!affichee}
          onClick={() => void choisir()}
        >
          {t.composants.photo.choisir}
        </Button>
      </div>
    </div>
  );

  return createPortal(vue, hote);
}
